import type { SupportedLanguage } from '@core/i18n/types';
import type { BeveragePairing, MenuBeveragePairing } from '../types/pairings';

export function getActivePairings(
  pairings: readonly BeveragePairing[] | null | undefined,
): BeveragePairing[] {
  if (!pairings) return [];
  return pairings.filter((p) => p.isActive !== false);
}

export function resolvePairing(
  pairing: BeveragePairing,
  language: string,
): BeveragePairing {
  const tr = pairing.translations?.[language as SupportedLanguage];
  const trName = tr?.name?.trim() ? tr.name : null;
  const trDescription = tr?.description?.trim() ? tr.description : null;
  return {
    ...pairing,
    name: trName ?? pairing.name,
    description: trDescription ?? pairing.description,
  };
}

export function resolvePairingName(
  id: number,
  fallbackName: string,
  beveragePairings: readonly BeveragePairing[] | null | undefined,
  language: string,
): string {
  const source = beveragePairings?.find((p) => p.id === id);
  if (!source) return fallbackName;
  return resolvePairing(source, language).name;
}

/**
 * Parse an API pairing price into a number. Prices arrive as strings
 * (e.g. "230", "199.50") but occasionally carry a currency prefix,
 * European thousands/decimal separators (e.g. "1.200,50"), or a single
 * thousands separator with no decimal component (e.g. "$1.200 ARS" /
 * "$1,200 USD" — a lone separator followed by exactly 3 digits reads as
 * thousands, not decimal: "1.200" and "1,200" both parse to 1200, while
 * "35.00" and "0,5" — 1-2 trailing digits — stay decimal). Non-numeric
 * input parses to 0 rather than NaN, since it feeds directly into fee math.
 */
export function parsePairingPrice(value: string | number | null | undefined): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (value == null) return 0;

  // Whitespace between two digits is grouping (fr/de "1 234,56", and the
  // NBSP or narrow NBSP a spreadsheet or CMS paste carries); drop it before
  // matching so the number does not stop at the first space. A space next to
  // a currency ("USD 85", "85 ARS") is not between digits and stays.
  const normalized = value.trim().replace(/(\d)[\s\u00a0\u202f]+(?=\d)/g, '$1');
  const match = normalized.match(/[\d.,]+/);
  if (!match) return 0;

  let numStr = match[0];
  const dotCount = (numStr.match(/\./g) ?? []).length;
  const commaCount = (numStr.match(/,/g) ?? []).length;

  if (dotCount > 0 && commaCount > 0) {
    // Both separators present: whichever comes last is the decimal separator.
    const lastDot = numStr.lastIndexOf('.');
    const lastComma = numStr.lastIndexOf(',');
    numStr = lastComma > lastDot
      ? numStr.replace(/\./g, '').replace(',', '.')
      : numStr.replace(/,/g, '');
  } else if (commaCount === 1) {
    const fraction = numStr.split(',')[1];
    numStr = fraction.length === 3 ? numStr.replace(',', '') : numStr.replace(',', '.');
  } else if (dotCount === 1) {
    const fraction = numStr.split('.')[1];
    if (fraction.length === 3) numStr = numStr.replace('.', '');
  } else if (commaCount > 1) {
    // Only one kind of separator, repeated: grouping ("1,234,567").
    numStr = numStr.replace(/,/g, '');
  } else if (dotCount > 1) {
    numStr = numStr.replace(/\./g, '');
  }

  const parsed = parseFloat(numStr);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * The beverage pairings offered by a specific menu, already localized and
 * price-overridden by the backend — unlike restaurant.beveragePairings,
 * which is override-blind.
 */
export function menuPairingsFor(
  menu: { beveragePairings?: MenuBeveragePairing[] } | null | undefined,
): MenuBeveragePairing[] {
  return menu?.beveragePairings ?? [];
}

/**
 * Whether the pairing step applies to a menu: it must allow pairings and
 * carry a non-empty menu-level list, which is the only list the step can
 * render. An absent list (a payload predating the field) offers nothing.
 */
export function menuOffersPairings(
  menu: { allowPairings?: boolean; beveragePairings?: MenuBeveragePairing[] } | null | undefined,
): boolean {
  return !!menu?.allowPairings && menuPairingsFor(menu).length > 0;
}

/**
 * The waiting-list menu the pairing step prices against: the first menu
 * the guest selected on the form when there is one, else the slot's first
 * menu (the legacy rule, which only mattered for mandatory-ness while every
 * menu shared one price list).
 */
export function selectedWlMenu<T extends { id: number }>(
  wlSlotMenus: readonly T[],
  wlMenus: readonly number[],
): T | undefined {
  return wlSlotMenus.find((m) => wlMenus.includes(m.id)) ?? wlSlotMenus[0];
}
